import {
  MedplumClient,
  createReference,
  getExtensionValue,
} from '@medplum/core';
import {
  Attachment,
  Bundle,
  BundleEntry,
  Condition,
  Encounter,
  Media,
  MedicationStatement,
  Observation,
  Patient,
  Practitioner,
  Procedure,
} from '@medplum/fhirtypes';
import { ARKON, EXTERNAL_CS, HL7, SYSTEMS } from '@/consts/systems.consts';
import { THERAPEUTIC_SERVICE_SYSTEM } from '@consts/therapeutic-service.consts';
import {
  createNewTherapeuticServiceProcedure,
  getServiceCode,
  getStartDate,
  getEndDate,
  getResponse,
  getContextEncounterId,
  setServiceCode,
  setStartAge,
  setResponse,
  prepareProcedureForSave,
} from '@/utils/procedure.utils';
import { HpoItem } from '@/types/hpo.types';
import { PhotoFile } from './photos/PhotoInput';
import { TherapeuticServiceInputItem } from '@/components/inputs/TherapeuticServiceInput';
import { TherapeuticServiceFormData } from './therapeuticServices/TherapeuticServiceModal';
import type { PsychomotorDevelopmentItem } from '@/types/psychomotor-development.types';
import { PhysicalGrowthData } from '@/components/inputs/PhysicalGrowthInput';
import { MeasurementAge } from '@/components/inputs/DateOrAgeSelector';
import {
  PHYSICAL_EXAM_SECTION_CODES,
  PhysicalExaminationData,
  VITAL_SIGNS_CODES,
} from '@/components/inputs/physical-examination/types';
import { createEmptyPhysicalExamination } from '@/components/inputs/physical-examination/physical-examination.utils';

/**
 * Safely parses a string value to a number.
 * Returns undefined if the value is empty, null, undefined, or not a valid number.
 * @param value - The string value to parse
 * @returns The parsed number or undefined if invalid
 */
function safeParseNumber(value: string | undefined | null): number | undefined {
  if (value === undefined || value === null || value.trim() === '') {
    return undefined;
  }
  const num = Number(value);
  return isNaN(num) ? undefined : num;
}

export type ReasonMode = 'text' | 'icd10';
type MeasurementType = 'height' | 'weight' | 'headCircumference';

function computeEffectiveDateTime(
  measurementAge: MeasurementAge | undefined,
  patientBirthDate: string | undefined,
  encounterStartDate: string | undefined
): string | undefined {
  if (!measurementAge) {
    return encounterStartDate;
  }

  if (measurementAge.type === 'today') {
    return encounterStartDate;
  }

  if (measurementAge.type === 'date' && measurementAge.date) {
    const date = measurementAge.date;
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  if (
    measurementAge.type === 'age' &&
    measurementAge.ageValue !== undefined &&
    patientBirthDate
  ) {
    try {
      const birthDate = new Date(patientBirthDate);
      const measurementDate = new Date(birthDate);

      if (measurementAge.ageUnit === 'months') {
        measurementDate.setMonth(
          birthDate.getMonth() + measurementAge.ageValue
        );
      } else if (measurementAge.ageUnit === 'years') {
        measurementDate.setFullYear(
          birthDate.getFullYear() + measurementAge.ageValue
        );
      }

      // Format as YYYY-MM-DD
      const year = measurementDate.getFullYear();
      const month = String(measurementDate.getMonth() + 1).padStart(2, '0');
      const day = String(measurementDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error computing effectiveDateTime from age:', error);
      return encounterStartDate;
    }
  }

  return encounterStartDate;
}

export interface ReportFileInput {
  description: string;
  date: string | undefined;
  file: File | null;
}

export function buildUpdatedEncounter(
  encounter: Encounter,
  clinicalNote: string
): Encounter {
  return {
    ...encounter,
    extension: [
      ...(encounter.extension?.filter(
        (ext) => ext.url !== ARKON.SD.CLINICAL_NOTES
      ) ?? []), // keep other extensions intact
      ...(clinicalNote
        ? [
            {
              url: ARKON.SD.CLINICAL_NOTES,
              valueString: clinicalNote,
            },
          ]
        : []),
    ],
  };
}

function buildBodyMeasurement(
  patient: Patient,
  encounter: Encounter,
  type: MeasurementType,
  value: number,
  existingObservations: Observation[],
  performer?: Practitioner,
  effectiveDateTime?: string
): BundleEntry[] {
  if (!value) {
    return [];
  }

  // LOINC codes + units mapping
  const measurementMap: Record<
    MeasurementType,
    { code: string; display: string; unit: string }
  > = {
    height: {
      code: EXTERNAL_CS.LOINC.CODES.BODY_HEIGHT,
      display: 'Body Height',
      unit: 'cm',
    },
    weight: {
      code: EXTERNAL_CS.LOINC.CODES.BODY_WEIGHT,
      display: 'Body Weight',
      unit: 'kg',
    },
    headCircumference: {
      code: EXTERNAL_CS.LOINC.CODES.HEAD_CIRCUMFERENCE,
      display: 'Head circumference',
      unit: 'cm',
    },
  };

  const { code, display, unit } = measurementMap[type];

  // Find existing observation for this type
  const existingObservation = existingObservations.find(
    (obs) => obs.code?.coding?.[0]?.code === code
  );

  // Create the value quantity (shared between new and updated observations)
  const valueQuantity = {
    value,
    unit,
    system: SYSTEMS.UCUM,
    code: unit,
  };

  if (existingObservation) {
    const observation: Observation = {
      ...existingObservation,
      valueQuantity,
      performer: performer
        ? [createReference(performer)]
        : existingObservation.performer,
      effectiveDateTime:
        effectiveDateTime ||
        existingObservation.effectiveDateTime ||
        encounter.period?.start,
    };

    return [
      {
        resource: observation,
        request: {
          method: 'PUT' as const,
          url: `Observation/${existingObservation.id}`,
        },
      },
    ];
  }

  const observation: Observation = {
    resourceType: 'Observation',
    status: 'final',
    category: [
      {
        coding: [
          {
            system: HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE,
            code: 'vital-signs',
          },
        ],
      },
    ],
    subject: createReference(patient),
    encounter: createReference(encounter),
    performer: performer ? [createReference(performer)] : undefined,
    code: {
      coding: [
        {
          system: SYSTEMS.LOINC,
          code,
          display,
        },
      ],
    },
    valueQuantity,
    effectiveDateTime: effectiveDateTime || encounter.period?.start,
  };

  return [
    {
      resource: observation,
      request: { method: 'POST' as const, url: 'Observation' },
    },
  ];
}

/**
 * Creates an attachment from a PhotoFile for saving to Media resource
 * @param medplum - The Medplum client instance
 * @param photoFile - The photo file to create attachment from
 * @returns Promise that resolves to the created attachment
 */
async function createAttachmentFromPhotoFile(
  medplum: MedplumClient,
  photoFile: PhotoFile
): Promise<Attachment> {
  const attachment = await medplum.createAttachment({
    data: photoFile.file,
    contentType: photoFile.file.type || 'image/jpeg',
    filename: photoFile.file.name,
  });

  // Ensure the attachment has the size property
  return {
    ...attachment,
    size: photoFile.file.size,
  };
}

/**
 * Builds a Media resource from a PhotoFile
 * @param patient - The patient resource
 * @param encounter - The encounter resource
 * @param photoFile - The photo file data
 * @param attachment - The attachment data
 * @returns The built Media resource
 */
function buildMediaResource(
  patient: Patient,
  encounter: Encounter,
  photoFile: PhotoFile,
  attachment: Attachment
): Media {
  const baseMedia: Media = {
    resourceType: 'Media',
    status: 'completed',
    type: { text: 'Image' },
    content: attachment,
    encounter: createReference(encounter),
    subject: createReference(patient),
    createdDateTime: photoFile.timestamp.toISOString(),
    extension: [
      {
        url: 'http://arkon.com/fhir/StructureDefinition/media-source',
        valueString: photoFile.source,
      },
    ],
  };

  return baseMedia;
}

/**
 * Builds bundle entries for photo files (Media resources)
 * @param medplum - The Medplum client instance
 * @param patient - The patient resource
 * @param encounter - The encounter resource
 * @param photos - Array of photo files to process
 * @param existingPhotos - Array of existing photo media resources
 * @returns Promise that resolves to array of bundle entries
 */
export async function buildPhotoFiles(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter,
  photos: PhotoFile[],
  existingPhotos: Media[] = []
): Promise<BundleEntry[]> {
  const entries: BundleEntry[] = [];

  // Create or update entries for new/modified photos
  for (const photo of photos) {
    try {
      if (photo.id) {
        continue;
      }
      const attachment = await createAttachmentFromPhotoFile(medplum, photo);

      const mediaResource = buildMediaResource(
        patient,
        encounter,
        photo,
        attachment
      );

      entries.push({
        resource: mediaResource,
        request: {
          method: 'POST',
          url: 'Media',
        },
      });
    } catch (error) {
      console.error('Error creating attachment for photo:', error);
    }
  }

  // Delete entries for photos that were removed
  const deleteEntries: BundleEntry[] = existingPhotos
    .filter((e) => !photos.some((p) => p.id === e.id))
    .map(
      (e) =>
        ({
          request: { method: 'DELETE', url: `Media/${e.id}` },
        }) as BundleEntry
    );

  return [...entries, ...deleteEntries];
}

export interface FormValues {
  notes: string;
}

// -----------------------------------------------------------------------------
// Prepare a MedicationStatement for saving to the server.
// This is used by useMedicationActions hook for immediate saves.
// For existing medications, preserves the original context (first encounter).
// For new medications, sets context to current encounter.
export function prepareMedicationForSave(
  patient: Patient,
  encounter: Encounter,
  medication: MedicationStatement
): MedicationStatement {
  // For existing medications (have an id), preserve the context already on the medication
  // For new medications, set context to current encounter if not already set
  const contextReference = medication.id
    ? medication.context
    : (medication.context ?? createReference(encounter));

  return {
    ...medication,
    resourceType: 'MedicationStatement',
    subject: medication.subject ?? createReference(patient),
    context: contextReference,
    dateAsserted:
      medication.dateAsserted || new Date().toISOString().split('T')[0],
  };
}

// Load ALL patient medications (longitudinal view) - not encounter-scoped.
export async function loadPatientMedications(
  medplum: MedplumClient,
  patient: Patient
): Promise<MedicationStatement[]> {
  if (!patient?.id) {
    return [];
  }

  // Query ALL patient medications (not filtered by encounter)
  const medications = await medplum.searchResources('MedicationStatement', {
    subject: `Patient/${patient.id}`,
    _sort: '-_lastUpdated',
    'status:not': 'entered-in-error',
  });

  return medications || [];
}

function mapProcedureToTherapeuticServiceFormData(
  procedure: Procedure
): TherapeuticServiceFormData {
  return {
    id: procedure.id,
    service: getServiceCode(procedure),
    startDate: getStartDate(procedure),
    terminationDate: getEndDate(procedure),
    response: getResponse(procedure) ?? '',
    status: procedure.status,
    encounterId: getContextEncounterId(procedure),
  };
}

export function buildTherapeuticServices(
  patient: Patient,
  encounter: Encounter,
  services: TherapeuticServiceInputItem[],
  existingProcedures: Procedure[] = []
): BundleEntry[] {
  const entries: BundleEntry[] = [];

  const existingMap = new Map(
    existingProcedures.map((procedure) => [procedure.id || '', procedure])
  );
  const processedIds = new Set<string>();

  for (const service of services) {
    if (!service.service?.coding?.[0]?.code) {
      continue;
    }

    // Build procedure using utility functions
    let procedure = createNewTherapeuticServiceProcedure(patient, encounter);
    procedure = setServiceCode(procedure, service.service);
    procedure = setStartAge(
      procedure,
      service.startAge !== null ? service.startAge : undefined
    );
    procedure = setResponse(procedure, service.response || undefined);

    // Prepare for save (validates and cleans)
    procedure = prepareProcedureForSave(procedure);

    if (service.id) {
      const existing = existingMap.get(service.id);
      if (existing?.id) {
        entries.push({
          resource: { ...procedure, id: existing.id },
          request: {
            method: 'PUT',
            url: `Procedure/${existing.id}`,
          },
        });
        processedIds.add(existing.id);
        continue;
      }
    }

    entries.push({
      resource: procedure,
      request: {
        method: 'POST',
        url: 'Procedure',
      },
    });
  }

  for (const [id] of existingMap.entries()) {
    if (id && !processedIds.has(id)) {
      entries.push({
        request: {
          method: 'DELETE',
          url: `Procedure/${id}`,
        },
      });
    }
  }

  return entries;
}

export async function loadTherapeuticServices(
  medplum: MedplumClient,
  patient: Patient
): Promise<TherapeuticServiceFormData[]> {
  if (!patient?.id) {
    return [];
  }

  // Filter at FHIR query level: therapeutic services (by code system) excluding soft-deleted
  const therapeuticProcedures = await medplum.searchResources('Procedure', {
    subject: `Patient/${patient.id}`,
    code: `${THERAPEUTIC_SERVICE_SYSTEM}|`,
    'status:not': 'entered-in-error',
  });

  return (
    therapeuticProcedures.map(mapProcedureToTherapeuticServiceFormData) || []
  );
}

// Psychomotor Development Observations
const PSYCHOMOTOR_DEVELOPMENT_CATEGORY = 'survey';
const PSYCHOMOTOR_DEVELOPMENT_CODE_MAP: Record<
  string,
  { code: string; display: string; system: string; invertBoolean?: boolean }
> = {
  'age-compatible': {
    code: '23397005', // SNOMED: Normal growth and development
    display: 'Normal growth and development',
    system: SYSTEMS.SNOMED,
  },
  'social-smile': {
    code: '225583004', // SNOMED: Social smile present
    display: 'Social smile present',
    system: SYSTEMS.SNOMED,
  },
  'head-support': {
    code: '840673007', // SNOMED: Head support
    display: 'Head support',
    system: SYSTEMS.SNOMED,
  },
  'independent-walking': {
    code: '165245003', // SNOMED: Independent walking
    display: 'Independent walking',
    system: SYSTEMS.SNOMED,
  },
  speech: {
    code: '1197010003', // SNOMED: Speech development
    display: 'Speech development',
    system: SYSTEMS.SNOMED,
  },
  'school-performance': {
    code: '315245004', // SNOMED: Deterioration in school performance (NOTE: boolean is inverted)
    display: 'Deterioration in school performance',
    system: SYSTEMS.SNOMED,
    invertBoolean: true, // User answers about good performance, but code represents deterioration
  },
};

export function buildPsychomotorDevelopment(
  patient: Patient,
  encounter: Encounter,
  items: PsychomotorDevelopmentItem[],
  performer?: Practitioner,
  existingObservations: Observation[] = []
): BundleEntry[] {
  const entries: BundleEntry[] = [];

  // Create a map of existing observations by code
  const existingMap = new Map(
    existingObservations
      .filter((obs) => obs.code?.coding?.[0]?.code)
      .map((obs) => [obs.code?.coding?.[0]?.code as string, obs])
  );

  // Track which existing observations we've processed
  const processedIds = new Set<string>();

  for (const item of items) {
    const codeInfo = PSYCHOMOTOR_DEVELOPMENT_CODE_MAP[item.type];
    if (!codeInfo) {
      continue;
    }

    // Skip if no value and no observations (empty item)
    if (item.value === null && !item.observations) {
      // If there's an existing observation, mark it for deletion
      const existing = existingMap.get(codeInfo.code);
      if (existing?.id) {
        entries.push({
          request: {
            method: 'DELETE',
            url: `Observation/${existing.id}`,
          },
        });
        processedIds.add(existing.id);
      }
      continue;
    }

    const observation: Observation = {
      resourceType: 'Observation',
      status: 'final',
      category: [
        {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE,
              code: PSYCHOMOTOR_DEVELOPMENT_CATEGORY,
              display: 'Survey',
            },
          ],
          text: 'Psychomotor Development',
        },
      ],
      subject: createReference(patient),
      encounter: createReference(encounter),
      performer: performer ? [createReference(performer)] : undefined,
      code: {
        coding: [
          {
            system: codeInfo.system,
            code: codeInfo.code,
            display: codeInfo.display,
          },
        ],
        text: codeInfo.display,
      },
      effectiveDateTime: encounter.period?.start || new Date().toISOString(),
    };

    // Add boolean value if present
    if (item.value !== null) {
      // Invert boolean for codes that represent the opposite condition
      // (e.g., "Deterioration in school performance" where true = good performance)
      observation.valueBoolean = codeInfo.invertBoolean
        ? !item.value
        : item.value;
    }

    // Add observations/notes if present
    if (item.observations) {
      observation.note = [{ text: item.observations }];
    }

    // Check if this observation already exists
    const existing = existingMap.get(codeInfo.code);
    if (existing?.id) {
      entries.push({
        resource: {
          ...observation,
          id: existing.id,
          meta: existing.meta, // Preserve metadata on updates
        },
        request: {
          method: 'PUT',
          url: `Observation/${existing.id}`,
        },
      });
      processedIds.add(existing.id);
    } else {
      entries.push({
        resource: observation,
        request: {
          method: 'POST',
          url: 'Observation',
        },
      });
    }
  }

  // Mark remaining existing observations for deletion (those not processed)
  for (const [_code, existing] of existingMap.entries()) {
    if (existing.id && !processedIds.has(existing.id)) {
      entries.push({
        request: {
          method: 'DELETE',
          url: `Observation/${existing.id}`,
        },
      });
    }
  }

  return entries;
}

/**
 * Immediately saves psychomotor development observations to the server
 * This is used when the user clicks "Add" or "Save" in the psychomotor development modal
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param encounter - Encounter resource
 * @param items - Psychomotor development items to save
 * @param performer - Optional practitioner who recorded the assessment
 * @returns Promise that resolves when save is complete
 */
export async function savePsychomotorDevelopment(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter,
  items: PsychomotorDevelopmentItem[],
  performer?: Practitioner
): Promise<void> {
  // Load existing observations
  const { existingObservations } = await loadEncounterPsychomotorDevelopment(
    medplum,
    patient,
    encounter
  );

  // Build bundle entries
  const entries = buildPsychomotorDevelopment(
    patient,
    encounter,
    items,
    performer,
    existingObservations
  );

  // If there are no entries, nothing to save
  if (entries.length === 0) {
    return;
  }

  // Create transaction bundle
  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };

  // Execute the transaction
  await medplum.executeBatch(bundle);
}

/**
 * Immediately deletes all psychomotor development observations from the server
 * This is used when the user clicks "Remove" in the psychomotor development list
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param encounter - Encounter resource
 * @returns Promise that resolves when delete is complete
 */
export async function deletePsychomotorDevelopment(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter
): Promise<void> {
  const entries: BundleEntry[] = [];

  // Load existing observations
  const { existingObservations } = await loadEncounterPsychomotorDevelopment(
    medplum,
    patient,
    encounter
  );

  // Delete all psychomotor development observations
  for (const observation of existingObservations) {
    if (observation.id) {
      entries.push({
        request: {
          method: 'DELETE',
          url: `Observation/${observation.id}`,
        },
      });
    }
  }

  // If there are no entries, nothing to delete
  if (entries.length === 0) {
    return;
  }

  // Create transaction bundle
  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };

  // Execute the transaction
  await medplum.executeBatch(bundle);
}

export async function loadEncounterPsychomotorDevelopment(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter | undefined
): Promise<{
  items: PsychomotorDevelopmentItem[];
  existingObservations: Observation[];
}> {
  if (!patient?.id || !encounter?.id) {
    return {
      items: [],
      existingObservations: [],
    };
  }

  // Search for psychomotor development observations
  // We'll search by category and then filter by the specific codes
  const allObservations = await medplum.searchResources('Observation', {
    subject: `Patient/${patient.id}`,
    encounter: `Encounter/${encounter.id}`,
    category: PSYCHOMOTOR_DEVELOPMENT_CATEGORY,
  });

  // Filter for psychomotor development observations by checking codes
  const psychomotorObservations = (allObservations || []).filter((obs) => {
    const code = obs.code?.coding?.[0]?.code;
    if (!code) {
      return false;
    }
    return Object.values(PSYCHOMOTOR_DEVELOPMENT_CODE_MAP).some(
      (codeInfo) => codeInfo.code === code
    );
  });

  // Create a map of observations by code
  const observationMap = new Map(
    psychomotorObservations.map((obs) => [
      obs.code?.coding?.[0]?.code as string,
      obs,
    ])
  );

  // Map observations to input items
  const items: PsychomotorDevelopmentItem[] = Object.entries(
    PSYCHOMOTOR_DEVELOPMENT_CODE_MAP
  ).map(([type, codeInfo]) => {
    const observation = observationMap.get(codeInfo.code);
    if (!observation) {
      return {
        type: type as PsychomotorDevelopmentItem['type'],
        value: null,
        observations: '',
      };
    }

    // Invert boolean when loading if the code represents the opposite condition
    let value: boolean | null = null;
    if (observation.valueBoolean !== undefined) {
      value = codeInfo.invertBoolean
        ? !observation.valueBoolean
        : observation.valueBoolean;
    }

    return {
      id: observation.id,
      type: type as PsychomotorDevelopmentItem['type'],
      value,
      observations: observation.note?.[0]?.text || '',
    };
  });

  return {
    items,
    existingObservations: psychomotorObservations,
  };
}

export async function loadPreviousEncounterPsychomotorDevelopment(
  medplum: MedplumClient,
  patient: Patient,
  currentEncounter: Encounter | undefined
): Promise<{
  items: PsychomotorDevelopmentItem[];
  encounterDate?: string;
} | null> {
  if (!patient?.id || !currentEncounter?.id) {
    return null;
  }

  // Get previous encounter
  const previousEncounter = await getPreviousEncounter(
    medplum,
    patient,
    currentEncounter
  );

  if (!previousEncounter?.id) {
    return null;
  }

  // Load psychomotor development for previous encounter
  const result = await loadEncounterPsychomotorDevelopment(
    medplum,
    patient,
    previousEncounter
  );

  return {
    items: result.items,
    encounterDate: previousEncounter.period?.start,
  };
}

export async function loadPreviousEncounterPhysicalGrowth(
  medplum: MedplumClient,
  patient: Patient,
  currentEncounter: Encounter | undefined
): Promise<{
  data: PhysicalGrowthData | null;
  encounterDate?: string;
} | null> {
  if (!patient?.id || !currentEncounter?.id) {
    return null;
  }

  // Get previous encounter
  const previousEncounter = await getPreviousEncounter(
    medplum,
    patient,
    currentEncounter
  );

  if (!previousEncounter?.id) {
    return null;
  }

  // Load physical growth for previous encounter
  const result = await loadEncounterPhysicalGrowth(
    medplum,
    patient,
    previousEncounter
  );

  return {
    data: result.data,
    encounterDate: previousEncounter.period?.start,
  };
}

// Physical Growth Observation
// Using SNOMED code: 58236001 - Normal growth (finding)
// valueBoolean: true = within normal limits, false = pathological
const PHYSICAL_GROWTH_CODE = '58236001';
const PHYSICAL_GROWTH_DISPLAY = 'Normal growth';

// Special marker for "normal" sections (no pathological findings)
const NORMAL_MARKER = '__NORMAL__';

export function buildPhysicalGrowth(
  patient: Patient,
  encounter: Encounter,
  data: PhysicalGrowthData | null,
  existingObservation: Observation | undefined,
  performer?: Practitioner,
  existingBodyMeasurements: Observation[] = []
): BundleEntry[] {
  const entries: BundleEntry[] = [];

  // Compute effectiveDateTime from measurementAge
  const effectiveDateTime = computeEffectiveDateTime(
    data?.measurementAge,
    patient.birthDate,
    encounter.period?.start
  );

  // Build body measurements (height, weight, headCircumference)
  if (data?.height) {
    entries.push(
      ...buildBodyMeasurement(
        patient,
        encounter,
        'height',
        Number(data.height),
        existingBodyMeasurements,
        performer,
        effectiveDateTime
      )
    );
  }
  if (data?.weight) {
    entries.push(
      ...buildBodyMeasurement(
        patient,
        encounter,
        'weight',
        Number(data.weight),
        existingBodyMeasurements,
        performer,
        effectiveDateTime
      )
    );
  }
  if (data?.headCircumference) {
    entries.push(
      ...buildBodyMeasurement(
        patient,
        encounter,
        'headCircumference',
        Number(data.headCircumference),
        existingBodyMeasurements,
        performer,
        effectiveDateTime
      )
    );
  }

  // Build physical growth assessment observation
  if (!data?.value) {
    if (existingObservation?.id) {
      entries.push({
        request: {
          method: 'DELETE',
          url: `Observation/${existingObservation.id}`,
        },
      });
    }
    return entries;
  }

  // Convert string value to boolean
  // 'within-normal-limits' -> true (normal growth)
  // 'pathological' -> false (not normal growth)
  const valueBoolean = data.value === 'within-normal-limits';

  const observation: Observation = {
    resourceType: 'Observation',
    status: 'final',
    category: [
      {
        coding: [
          {
            system: HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE,
            code: 'survey',
            display: 'Survey',
          },
        ],
        text: 'Physical Growth',
      },
    ],
    subject: createReference(patient),
    encounter: createReference(encounter),
    performer: performer ? [createReference(performer)] : undefined,
    code: {
      coding: [
        {
          system: SYSTEMS.SNOMED,
          code: PHYSICAL_GROWTH_CODE,
          display: PHYSICAL_GROWTH_DISPLAY,
        },
      ],
      text: PHYSICAL_GROWTH_DISPLAY,
    },
    valueBoolean,
    effectiveDateTime: encounter.period?.start || new Date().toISOString(),
  };

  // Add explanation note if explanation is provided
  if (data.comments) {
    observation.note = [{ text: data.comments }];
  }

  if (existingObservation?.id) {
    entries.push({
      resource: {
        ...observation,
        id: existingObservation.id,
        meta: existingObservation.meta, // Preserve metadata on updates
      },
      request: {
        method: 'PUT',
        url: `Observation/${existingObservation.id}`,
      },
    });
  } else {
    entries.push({
      resource: observation,
      request: {
        method: 'POST',
        url: 'Observation',
      },
    });
  }
  return entries;
}

export async function loadEncounterPhysicalGrowth(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter | undefined
): Promise<{
  data: PhysicalGrowthData | null;
  existingObservation: Observation | undefined;
}> {
  if (!patient?.id || !encounter?.id) {
    return {
      data: null,
      existingObservation: undefined,
    };
  }

  // Load body measurements
  const bodyMeasurements = await medplum.searchResources('Observation', {
    subject: `Patient/${patient.id}`,
    encounter: `Encounter/${encounter.id}`,
    category: 'vital-signs',
  });

  let height = '';
  let weight = '';
  let headCircumference = '';

  for (const obs of bodyMeasurements || []) {
    const code = obs.code?.coding?.[0]?.code;
    const value = obs.valueQuantity?.value;

    if (code && value !== undefined) {
      switch (code) {
        case EXTERNAL_CS.LOINC.CODES.BODY_HEIGHT:
          height = value.toString();
          break;
        case EXTERNAL_CS.LOINC.CODES.BODY_WEIGHT:
          weight = value.toString();
          break;
        case EXTERNAL_CS.LOINC.CODES.HEAD_CIRCUMFERENCE:
          headCircumference = value.toString();
          break;
      }
    }
  }

  // Load physical growth assessment
  const physicalGrowthObservation = await medplum.searchOne('Observation', {
    subject: `Patient/${patient.id}`,
    encounter: `Encounter/${encounter.id}`,
    category: `${HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE}|survey`,
    code: `${SYSTEMS.SNOMED}|${PHYSICAL_GROWTH_CODE}`,
  });

  if (!physicalGrowthObservation) {
    // Return data with measurements even if no assessment
    return {
      data: {
        value: null,
        comments: '',
        height,
        weight,
        headCircumference,
      },
      existingObservation: undefined,
    };
  }

  // Extract value from valueBoolean and convert to string
  // true -> 'within-normal-limits' (normal growth)
  // false -> 'pathological' (not normal growth)
  let value: PhysicalGrowthData['value'] = null;
  if (physicalGrowthObservation.valueBoolean !== undefined) {
    value = physicalGrowthObservation.valueBoolean
      ? 'within-normal-limits'
      : 'pathological';
  }

  // Extract comments from notes
  const comments = physicalGrowthObservation.note?.[0]?.text || '';

  const data: PhysicalGrowthData = {
    value,
    comments,
    height,
    weight,
    headCircumference,
  };

  return {
    data,
    existingObservation: physicalGrowthObservation,
  };
}

/**
 * Immediately saves physical growth observations to the server
 * This is used when the user clicks "Add" or "Save" in the physical growth modal
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param encounter - Encounter resource
 * @param data - Physical growth data to save
 * @param performer - Optional practitioner who recorded the measurement
 * @returns Promise that resolves when save is complete
 */
export async function savePhysicalGrowth(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter,
  data: PhysicalGrowthData | null,
  performer?: Practitioner
): Promise<void> {
  // Load existing observations and body measurements
  const result = await loadEncounterPhysicalGrowth(medplum, patient, encounter);

  // Load existing body measurements for proper update handling
  const bodyMeasurements = await medplum.searchResources('Observation', {
    subject: `Patient/${patient.id}`,
    encounter: `Encounter/${encounter.id}`,
    category: 'vital-signs',
  });

  // Build bundle entries
  const entries = buildPhysicalGrowth(
    patient,
    encounter,
    data,
    result.existingObservation,
    performer,
    bodyMeasurements || []
  );

  // If there are no entries, nothing to save
  if (entries.length === 0) {
    return;
  }

  // Create transaction bundle
  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };

  // Execute the transaction
  await medplum.executeBatch(bundle);
}

/**
 * Immediately deletes physical growth observations from the server
 * This is used when the user clicks "Remove" in the physical growth list
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param encounter - Encounter resource
 * @returns Promise that resolves when delete is complete
 */
export async function deletePhysicalGrowth(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter
): Promise<void> {
  const entries: BundleEntry[] = [];

  // Load existing physical growth observation
  const result = await loadEncounterPhysicalGrowth(medplum, patient, encounter);

  // Delete the physical growth assessment observation
  if (result.existingObservation?.id) {
    entries.push({
      request: {
        method: 'DELETE',
        url: `Observation/${result.existingObservation.id}`,
      },
    });
  }

  // Load and delete all body measurements (weight, height, head circumference)
  const bodyMeasurements = await medplum.searchResources('Observation', {
    subject: `Patient/${patient.id}`,
    encounter: `Encounter/${encounter.id}`,
    category: 'vital-signs',
  });

  if (bodyMeasurements) {
    for (const measurement of bodyMeasurements) {
      if (measurement.id) {
        entries.push({
          request: {
            method: 'DELETE',
            url: `Observation/${measurement.id}`,
          },
        });
      }
    }
  }

  // If there are no entries, nothing to delete
  if (entries.length === 0) {
    return;
  }

  // Create transaction bundle
  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };

  // Execute the transaction
  await medplum.executeBatch(bundle);
}

// Photos
/**
 * Loads photos (Media resources) for an encounter
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param encounter - Encounter resource
 * @returns Promise that resolves to photos array and existing media resources
 */
export async function loadEncounterPhotos(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter | undefined
): Promise<{
  photos: PhotoFile[];
  existingMedia: Media[];
}> {
  if (!patient?.id || !encounter?.id) {
    return {
      photos: [],
      existingMedia: [],
    };
  }

  const existingMedia = await medplum.searchResources('Media', {
    subject: `Patient/${patient.id}`,
    encounter: `Encounter/${encounter.id}`,
    type: 'Image',
  });

  const photos: PhotoFile[] = (existingMedia || []).map((media) => {
    const attachment = media.content;

    // Get source from extension
    const sourceExtension = media.extension?.find(
      (ext) => ext.url === ARKON.SD.MEDIA_SOURCE
    );
    const source = (
      sourceExtension?.valueString === 'camera' ? 'camera' : 'upload'
    ) as 'camera' | 'upload';

    // Calculate file size from attachment size or estimate from content type
    const fileSize = attachment?.size || 0;
    const fileName = attachment?.title || `photo_${media.id}.jpg`;
    const fileType = attachment?.contentType || 'image/jpeg';

    // Create a File-like object that preserves the size
    // We create a real File object first, then create a proxy-like object with the correct size
    const baseFile = new File([], fileName, { type: fileType });
    const file = {
      ...baseFile,
      name: fileName,
      size: fileSize,
      type: fileType,
      lastModified: media.createdDateTime
        ? new Date(media.createdDateTime).getTime()
        : Date.now(),
    } as File;

    return {
      id: media.id || '',
      file,
      preview: attachment?.url || '',
      timestamp: media.createdDateTime
        ? new Date(media.createdDateTime)
        : new Date(),
      source,
    };
  });

  return {
    photos,
    existingMedia: existingMedia || [],
  };
}

/**
 * Immediately saves photos to the server
 * This is used when the user clicks "Add" or "Save" in the photos modal
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param encounter - Encounter resource
 * @param photos - Photos to save
 * @returns Promise that resolves when save is complete
 */
export async function savePhotos(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter,
  photos: PhotoFile[]
): Promise<void> {
  // Load existing photos
  const { existingMedia } = await loadEncounterPhotos(
    medplum,
    patient,
    encounter
  );

  // Build bundle entries
  const entries = await buildPhotoFiles(
    medplum,
    patient,
    encounter,
    photos,
    existingMedia
  );

  // If there are no entries, nothing to save
  if (entries.length === 0) {
    return;
  }

  // Create transaction bundle
  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };

  // Execute the transaction
  await medplum.executeBatch(bundle);
}

/**
 * Immediately deletes all photos from the server
 * This is used when the user clicks "Remove" in the photos list
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param encounter - Encounter resource
 * @returns Promise that resolves when delete is complete
 */
export async function deletePhotos(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter
): Promise<void> {
  const entries: BundleEntry[] = [];

  // Load existing photos
  const { existingMedia } = await loadEncounterPhotos(
    medplum,
    patient,
    encounter
  );

  // Delete all photos
  for (const media of existingMedia) {
    if (media.id) {
      entries.push({
        request: {
          method: 'DELETE',
          url: `Media/${media.id}`,
        },
      });
    }
  }

  // If there are no entries, nothing to delete
  if (entries.length === 0) {
    return;
  }

  // Create transaction bundle
  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };

  // Execute the transaction
  await medplum.executeBatch(bundle);
}

export async function buildExaminationBundle(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter,
  formValues: FormValues,
  loaded: EncounterFormData | null,
  photos: PhotoFile[],
  performer?: Practitioner,
  psychomotorDevelopment: PsychomotorDevelopmentItem[] = [],
  physicalGrowth: PhysicalGrowthData | null = null
): Promise<Bundle> {
  const entries: BundleEntry[] = [];

  // Updated Encounter
  const updatedEncounter = buildUpdatedEncounter(encounter, formValues.notes);

  entries.push({
    resource: updatedEncounter,
    request: { method: 'PUT', url: `Encounter/${encounter.id}` },
  });

  // Physical Growth (includes body measurements and assessment)
  entries.push(
    ...buildPhysicalGrowth(
      patient,
      updatedEncounter,
      physicalGrowth,
      loaded?.existingResources.physicalGrowth,
      performer,
      loaded?.existingResources.bodyMeasurements ?? []
    )
  );

  // Photo files
  const photoEntries = await buildPhotoFiles(
    medplum,
    patient,
    updatedEncounter,
    photos,
    loaded?.existingResources.photos ?? []
  );
  entries.push(...photoEntries);

  // Psychomotor Development
  entries.push(
    ...buildPsychomotorDevelopment(
      patient,
      updatedEncounter,
      psychomotorDevelopment,
      performer,
      loaded?.existingResources.psychomotorDevelopment ?? []
    )
  );

  // Build the final bundle
  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };
}

export async function getPreviousEncounter(
  medplum: MedplumClient,
  patient: Patient,
  currentEncounter: Encounter | undefined
): Promise<Encounter | undefined> {
  if (!currentEncounter) {
    return undefined;
  }

  // Fetch all encounters for this patient
  const encounters = await medplum.searchResources('Encounter', {
    subject: `Patient/${patient.id}`,
    _sort: '-date',
  });

  if (!encounters || encounters.length === 0) {
    return undefined;
  }

  // Sort explicitly by start date
  const sorted = [...encounters].sort(
    (a, b) =>
      new Date(a.period?.start ?? 0).getTime() -
      new Date(b.period?.start ?? 0).getTime()
  );

  // Find current encounter index
  const idx = sorted.findIndex((e) => e.id === currentEncounter.id);
  if (idx <= 0) {
    return undefined; // no previous encounter
  }

  return sorted[idx - 1];
}
export interface EncounterFormData {
  notes?: string;
  hpoTerms: HpoItem[];
  photos: PhotoFile[];
  psychomotorDevelopment: PsychomotorDevelopmentItem[];
  physicalGrowth: PhysicalGrowthData | null;

  // Store the actual resources for efficient updates
  existingResources: {
    bodyMeasurements: Observation[];
    hpoConditions: Condition[];
    photos: Media[];
    psychomotorDevelopment: Observation[];
    physicalGrowth: Observation | undefined;
  };
}

export async function loadEncounterFormData(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter | undefined
): Promise<EncounterFormData> {
  if (!encounter) {
    return {
      hpoTerms: [],
      photos: [],
      psychomotorDevelopment: [],
      physicalGrowth: null,

      existingResources: {
        bodyMeasurements: [],
        hpoConditions: [],
        photos: [],
        psychomotorDevelopment: [],
        physicalGrowth: undefined,
      },
    };
  }

  const formData: EncounterFormData = {
    hpoTerms: [],
    photos: [],
    psychomotorDevelopment: [],
    physicalGrowth: null,

    existingResources: {
      bodyMeasurements: [],
      hpoConditions: [],
      photos: [],
      psychomotorDevelopment: [],
      physicalGrowth: undefined,
    },
  };

  try {
    const [
      observations,
      hpoConditions,
      existingPhotos,
      psychomotorResult,
      physicalGrowthResult,
    ] = await Promise.all([
      medplum.searchResources('Observation', {
        subject: `Patient/${patient.id}`,
        encounter: `Encounter/${encounter.id}`,
        category: 'vital-signs',
      }),
      medplum.searchResources('Condition', {
        subject: `Patient/${patient.id}`,
        encounter: `Encounter/${encounter.id}`,
        code: `${SYSTEMS.HPO}|`,
      }),
      medplum.searchResources('Media', {
        subject: `Patient/${patient.id}`,
        encounter: `Encounter/${encounter.id}`,
        type: 'Image',
      }),
      loadEncounterPsychomotorDevelopment(medplum, patient, encounter),
      loadEncounterPhysicalGrowth(medplum, patient, encounter),
    ]);

    formData.existingResources.bodyMeasurements = observations || [];

    formData.existingResources.hpoConditions = hpoConditions || [];
    formData.hpoTerms = (hpoConditions || []).map((condition) => ({
      id: condition.code?.coding?.[0]?.code || '',
      name: condition.code?.coding?.[0]?.display || condition.code?.text || '',
      value: condition.note?.[0]?.text || '',
    }));

    // Load clinical notes
    const clinicalNotes = getExtensionValue(encounter, ARKON.SD.CLINICAL_NOTES);
    if (clinicalNotes) {
      formData.notes = clinicalNotes as string;
    }

    formData.existingResources.photos = existingPhotos || [];
    formData.photos = (existingPhotos || []).map((media) => {
      const attachment = media.content;

      // Get source from extension
      const sourceExtension = media.extension?.find(
        (ext) =>
          ext.url === 'http://arkon.com/fhir/StructureDefinition/media-source'
      );
      const source = (
        sourceExtension?.valueString === 'camera' ? 'camera' : 'upload'
      ) as 'camera' | 'upload';

      // Calculate file size from attachment size or estimate from content type
      const fileSize = attachment?.size || 0;
      const fileName = attachment?.title || `photo_${media.id}.jpg`;
      const fileType = attachment?.contentType || 'image/jpeg';

      // Create a File-like object that preserves the size
      const baseFile = new File([], fileName, { type: fileType });
      const file = {
        ...baseFile,
        name: fileName,
        size: fileSize,
        type: fileType,
        lastModified: media.createdDateTime
          ? new Date(media.createdDateTime).getTime()
          : Date.now(),
      } as File;

      return {
        id: media.id || '',
        file,
        preview: attachment?.url || '',
        timestamp: media.createdDateTime
          ? new Date(media.createdDateTime)
          : new Date(),
        source,
      };
    });

    formData.existingResources.psychomotorDevelopment =
      psychomotorResult.existingObservations;
    formData.psychomotorDevelopment = psychomotorResult.items;

    formData.existingResources.physicalGrowth =
      physicalGrowthResult.existingObservation;
    formData.physicalGrowth = physicalGrowthResult.data;
  } catch (error) {
    console.error('Error loading encounter form data:', error);
  }

  return formData;
}

// =============================================================================
// Physical Examination
// =============================================================================
function formatExamSectionValue(
  sectionData: Record<string, string | boolean | undefined>
): string | null {
  const entries = Object.entries(sectionData).filter(
    ([key, value]) =>
      key !== 'normal' && typeof value === 'string' && value.trim() !== ''
  );
  if (entries.length === 0) {
    return null;
  }
  return entries.map(([key, value]) => `${key}: ${value}`).join('\n');
}

function parseExamSectionValue(
  valueString: string | undefined,
  fields: string[]
): Record<string, string> {
  const result: Record<string, string> = {};
  fields.forEach((field) => {
    result[field] = '';
  });

  if (!valueString) {
    return result;
  }

  const lines = valueString.split('\n');
  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      if (fields.includes(key)) {
        result[key] = value;
      }
    }
  }

  return result;
}

export function buildPhysicalExamination(
  patient: Patient,
  encounter: Encounter,
  data: PhysicalExaminationData | null,
  existingObservations: Observation[],
  performer?: Practitioner
): BundleEntry[] {
  if (!data) {
    // If no data, delete all existing observations
    return existingObservations
      .filter((obs) => obs.id)
      .map((obs) => ({
        request: {
          method: 'DELETE' as const,
          url: `Observation/${obs.id}`,
        },
      }));
  }

  const entries: BundleEntry[] = [];
  const processedIds = new Set<string>();
  const effectiveDateTime = encounter.period?.start || new Date().toISOString();

  // Build Blood Pressure observation (using component for systolic/diastolic)
  if (
    data.vitalSigns.bloodPressureSystolic ||
    data.vitalSigns.bloodPressureDiastolic
  ) {
    const existingBP = existingObservations.find(
      (obs) =>
        obs.code?.coding?.[0]?.code ===
        EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_PANEL
    );

    const bpObservation: Observation = {
      resourceType: 'Observation',
      status: 'final',
      category: [
        {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE,
              code: 'vital-signs',
              display: 'Vital Signs',
            },
          ],
        },
      ],
      code: {
        coding: [
          {
            system: SYSTEMS.LOINC,
            code: EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_PANEL,
            display: 'Blood pressure panel',
          },
        ],
      },
      subject: createReference(patient),
      encounter: createReference(encounter),
      performer: performer ? [createReference(performer)] : undefined,
      effectiveDateTime,
      component: [],
    };

    const systolicValue = safeParseNumber(
      data.vitalSigns.bloodPressureSystolic
    );
    if (systolicValue !== undefined && bpObservation.component) {
      bpObservation.component.push({
        code: {
          coding: [
            {
              system: SYSTEMS.LOINC,
              code: EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_SYSTOLIC,
              display: 'Systolic blood pressure',
            },
          ],
        },
        valueQuantity: {
          value: systolicValue,
          unit: 'mmHg',
          system: SYSTEMS.UCUM,
          code: 'mm[Hg]',
        },
      });
    }

    const diastolicValue = safeParseNumber(
      data.vitalSigns.bloodPressureDiastolic
    );
    if (diastolicValue !== undefined && bpObservation.component) {
      bpObservation.component.push({
        code: {
          coding: [
            {
              system: SYSTEMS.LOINC,
              code: EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_DIASTOLIC,
              display: 'Diastolic blood pressure',
            },
          ],
        },
        valueQuantity: {
          value: diastolicValue,
          unit: 'mmHg',
          system: SYSTEMS.UCUM,
          code: 'mm[Hg]',
        },
      });
    }

    if (existingBP?.id) {
      entries.push({
        resource: {
          ...bpObservation,
          id: existingBP.id,
          meta: existingBP.meta,
        },
        request: { method: 'PUT', url: `Observation/${existingBP.id}` },
      });
      processedIds.add(existingBP.id);
    } else {
      entries.push({
        resource: bpObservation,
        request: { method: 'POST', url: 'Observation' },
      });
    }
  }

  // Build other vital signs observations
  for (const [key, codeInfo] of Object.entries(VITAL_SIGNS_CODES)) {
    const rawValue = data.vitalSigns[key as keyof typeof data.vitalSigns];
    const parsedValue = safeParseNumber(rawValue);
    const existing = existingObservations.find(
      (obs) => obs.code?.coding?.[0]?.code === codeInfo.code
    );

    if (parsedValue !== undefined) {
      const observation: Observation = {
        resourceType: 'Observation',
        status: 'final',
        category: [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE,
                code: 'vital-signs',
                display: 'Vital Signs',
              },
            ],
          },
        ],
        code: {
          coding: [
            {
              system: SYSTEMS.LOINC,
              code: codeInfo.code,
              display: codeInfo.display,
            },
          ],
        },
        subject: createReference(patient),
        encounter: createReference(encounter),
        performer: performer ? [createReference(performer)] : undefined,
        effectiveDateTime,
        valueQuantity: {
          value: parsedValue,
          unit: codeInfo.unit,
          system: SYSTEMS.UCUM,
          code: codeInfo.unit,
        },
      };

      if (existing?.id) {
        entries.push({
          resource: { ...observation, id: existing.id, meta: existing.meta },
          request: { method: 'PUT', url: `Observation/${existing.id}` },
        });
        processedIds.add(existing.id);
      } else {
        entries.push({
          resource: observation,
          request: { method: 'POST', url: 'Observation' },
        });
      }
    } else if (existing?.id) {
      // Delete existing if value is cleared
      entries.push({
        request: { method: 'DELETE', url: `Observation/${existing.id}` },
      });
      processedIds.add(existing.id);
    }
  }

  // Build examination section observations
  const examSections: {
    key: keyof PhysicalExaminationData;
    fields: string[];
  }[] = [
    { key: 'headNeck', fields: ['cranium', 'eyes', 'ent', 'neck'] },
    { key: 'thorax', fields: ['respiratory', 'cardiovascular'] },
    {
      key: 'abdomen',
      fields: ['inspection', 'auscultation', 'palpation', 'percussion'],
    },
    {
      key: 'extremities',
      fields: ['inspection', 'edema', 'skinHair', 'pulses'],
    },
    {
      key: 'neurological',
      fields: [
        'alertnessOrientation',
        'speech',
        'cranialNerves',
        'muscleStrength',
        'sensation',
        'gaitBalance',
        'cerebellarSigns',
      ],
    },
  ];

  for (const section of examSections) {
    const codeInfo = PHYSICAL_EXAM_SECTION_CODES[section.key];
    const sectionData = data[section.key] as Record<
      string,
      string | boolean | undefined
    >;
    const isNormal = sectionData.normal === true;
    const valueString = isNormal
      ? NORMAL_MARKER
      : formatExamSectionValue(sectionData);
    const existing = existingObservations.find(
      (obs) => obs.code?.coding?.[0]?.code === codeInfo.code
    );

    if (valueString) {
      const observation: Observation = {
        resourceType: 'Observation',
        status: 'final',
        category: [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE,
                code: 'exam',
                display: 'Exam',
              },
            ],
          },
        ],
        code: {
          coding: [
            {
              system: ARKON.CS.PHYSICAL_EXAMINATION.BASE,
              code: codeInfo.code,
              display: codeInfo.display,
            },
          ],
        },
        subject: createReference(patient),
        encounter: createReference(encounter),
        performer: performer ? [createReference(performer)] : undefined,
        effectiveDateTime,
        valueString,
        // Add interpretation for normal findings
        ...(isNormal && {
          interpretation: [
            {
              coding: [
                {
                  system: HL7.TERMINOLOGY.CS.V3.OBSERVATION_INTERPRETATION.BASE,
                  code: HL7.TERMINOLOGY.CS.V3.OBSERVATION_INTERPRETATION.CODES
                    .NORMAL,
                  display: 'Normal',
                },
              ],
              text: 'Χωρίς παθολογικά ευρήματα',
            },
          ],
        }),
      };

      if (existing?.id) {
        entries.push({
          resource: { ...observation, id: existing.id, meta: existing.meta },
          request: { method: 'PUT', url: `Observation/${existing.id}` },
        });
        processedIds.add(existing.id);
      } else {
        entries.push({
          resource: observation,
          request: { method: 'POST', url: 'Observation' },
        });
      }
    } else if (existing?.id) {
      // Delete existing if all fields are cleared
      entries.push({
        request: { method: 'DELETE', url: `Observation/${existing.id}` },
      });
      processedIds.add(existing.id);
    }
  }

  // Delete any remaining unprocessed BP observations
  const existingBP = existingObservations.find(
    (obs) =>
      obs.code?.coding?.[0]?.code ===
        EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_PANEL && obs.id
  );
  if (
    existingBP?.id &&
    !processedIds.has(existingBP.id) &&
    !data.vitalSigns.bloodPressureSystolic &&
    !data.vitalSigns.bloodPressureDiastolic
  ) {
    entries.push({
      request: { method: 'DELETE', url: `Observation/${existingBP.id}` },
    });
  }

  return entries;
}

export async function loadEncounterPhysicalExamination(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter | undefined
): Promise<{
  data: PhysicalExaminationData;
  existingObservations: Observation[];
}> {
  const emptyData = createEmptyPhysicalExamination();

  if (!patient?.id || !encounter?.id) {
    return {
      data: emptyData,
      existingObservations: [],
    };
  }

  try {
    // Load vital signs observations
    const vitalSignsObs = await medplum.searchResources('Observation', {
      subject: `Patient/${patient.id}`,
      encounter: `Encounter/${encounter.id}`,
      category: 'vital-signs',
    });

    // Load exam observations
    const examObs = await medplum.searchResources('Observation', {
      subject: `Patient/${patient.id}`,
      encounter: `Encounter/${encounter.id}`,
      category: 'exam',
    });

    const allObservations = [...(vitalSignsObs || []), ...(examObs || [])];

    // Parse vital signs
    const data: PhysicalExaminationData = createEmptyPhysicalExamination();

    // Parse blood pressure
    const bpObs = vitalSignsObs?.find(
      (obs) =>
        obs.code?.coding?.[0]?.code ===
        EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_PANEL
    );
    if (bpObs?.component) {
      for (const comp of bpObs.component) {
        const code = comp.code?.coding?.[0]?.code;
        if (code === EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_SYSTOLIC) {
          data.vitalSigns.bloodPressureSystolic =
            comp.valueQuantity?.value?.toString() || '';
        } else if (code === EXTERNAL_CS.LOINC.CODES.BLOOD_PRESSURE_DIASTOLIC) {
          data.vitalSigns.bloodPressureDiastolic =
            comp.valueQuantity?.value?.toString() || '';
        }
      }
    }

    // Parse other vital signs
    for (const [key, codeInfo] of Object.entries(VITAL_SIGNS_CODES)) {
      const obs = vitalSignsObs?.find(
        (o) => o.code?.coding?.[0]?.code === codeInfo.code
      );
      if (obs?.valueQuantity?.value !== undefined) {
        (data.vitalSigns as Record<string, string>)[key] =
          obs.valueQuantity.value.toString();
      }
    }

    // Parse exam sections
    const examSections: {
      key: keyof PhysicalExaminationData;
      fields: string[];
    }[] = [
      { key: 'headNeck', fields: ['cranium', 'eyes', 'ent', 'neck'] },
      { key: 'thorax', fields: ['respiratory', 'cardiovascular'] },
      {
        key: 'abdomen',
        fields: ['inspection', 'auscultation', 'palpation', 'percussion'],
      },
      {
        key: 'extremities',
        fields: ['inspection', 'edema', 'skinHair', 'pulses'],
      },
      {
        key: 'neurological',
        fields: [
          'alertnessOrientation',
          'speech',
          'cranialNerves',
          'muscleStrength',
          'sensation',
          'gaitBalance',
          'cerebellarSigns',
        ],
      },
    ];

    for (const section of examSections) {
      const codeInfo = PHYSICAL_EXAM_SECTION_CODES[section.key];
      const obs = examObs?.find(
        (o) => o.code?.coding?.[0]?.code === codeInfo.code
      );
      if (obs?.valueString) {
        // Check if this is a "normal" observation
        const isNormal =
          obs.valueString === NORMAL_MARKER ||
          obs.interpretation?.some((i) =>
            i.coding?.some(
              (c) =>
                c.code ===
                HL7.TERMINOLOGY.CS.V3.OBSERVATION_INTERPRETATION.CODES.NORMAL
            )
          );

        if (isNormal) {
          // Set normal flag and clear text fields
          (data[section.key] as Record<string, string | boolean>).normal = true;
        } else {
          (data[section.key] as Record<string, string | boolean>) = {
            ...parseExamSectionValue(obs.valueString, section.fields),
            normal: false,
          };
        }
      }
    }

    return {
      data,
      existingObservations: allObservations,
    };
  } catch (error) {
    console.error('Error loading physical examination data:', error);
    return {
      data: emptyData,
      existingObservations: [],
    };
  }
}

export async function savePhysicalExamination(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter,
  data: PhysicalExaminationData | null,
  performer?: Practitioner
): Promise<void> {
  try {
    const { existingObservations } = await loadEncounterPhysicalExamination(
      medplum,
      patient,
      encounter
    );

    const entries = buildPhysicalExamination(
      patient,
      encounter,
      data,
      existingObservations,
      performer
    );

    if (entries.length === 0) {
      return;
    }

    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: entries,
    };

    await medplum.executeBatch(bundle);
  } catch (error) {
    console.error('Error saving physical examination:', error);
    throw error;
  }
}

export async function deletePhysicalExamination(
  medplum: MedplumClient,
  patient: Patient,
  encounter: Encounter
): Promise<void> {
  try {
    const { existingObservations } = await loadEncounterPhysicalExamination(
      medplum,
      patient,
      encounter
    );

    const entries: BundleEntry[] = existingObservations
      .filter((obs) => obs.id)
      .map((obs) => ({
        request: {
          method: 'DELETE' as const,
          url: `Observation/${obs.id}`,
        },
      }));

    if (entries.length === 0) {
      return;
    }

    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: entries,
    };

    await medplum.executeBatch(bundle);
  } catch (error) {
    console.error('Error deleting physical examination:', error);
    throw error;
  }
}
